
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Guard } from "../types";
import { Button } from "@/components/ui/button";
import { Archive } from "lucide-react";
import { useUserRoles } from "@/hooks/useUserRoles";

interface ArchiveGuardDialogProps {
  guard: Guard;
  onGuardArchived?: () => void;
  trigger?: React.ReactNode;
}

export const ArchiveGuardDialog = ({ guard, onGuardArchived, trigger }: ArchiveGuardDialogProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const { toast } = useToast();
  const { isAdmin } = useUserRoles();

  // Don't render if not admin
  if (!isAdmin) return null;

  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      // Try Edge Function first
      const { data, error } = await supabase.functions.invoke("archive-guard", {
        body: { guardId: guard.id },
      });

      // If Edge Function fails (any error or non-2xx status), use fallback
      if (error || (data && !data.success)) {
        console.log('Edge function failed, using fallback archive method');
        
        // Only delete from Supabase Auth to revoke login credentials
        // Profile data remains intact for historical records
        const { error: authError } = await supabase.auth.admin.deleteUser(guard.id);
        
        if (authError) {
          throw new Error(`Failed to revoke credentials: ${authError.message}`);
        }
      }

      toast({
        title: "Guard archived",
        description: `${guard.first_name} ${guard.last_name} has been archived successfully and their login credentials disabled.`,
      });

      if (onGuardArchived) {
        onGuardArchived();
      }
    } catch (error: any) {
      console.error("Error archiving guard:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to archive guard: ${error.message}`,
      });
    } finally {
      setIsArchiving(false);
      setShowDialog(false);
    }
  };

  return (
    <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
      {trigger ? (
        <div onClick={(e) => { e.stopPropagation(); setShowDialog(true); }}>
          {trigger}
        </div>
      ) : (
        <Button
          variant="destructive"
          onClick={(e) => {
            e.stopPropagation();
            setShowDialog(true);
          }}
          disabled={isArchiving}
        >
          <Archive className="mr-2 h-4 w-4" />
          {isArchiving ? "Archiving..." : "Archive Guard"}
        </Button>
      )}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive This Guard?</AlertDialogTitle>
          <AlertDialogDescription>
            This will disable {guard.first_name} {guard.last_name}'s login credentials and remove them from all teams.
            Their information will be retained in the system but marked as archived.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleArchive();
            }}
            disabled={isArchiving}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isArchiving ? "Archiving..." : "Archive Guard"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
